import { TEST_ID_PLAYER_KANBAN } from "e2e/testId";
import { Check, X } from "lucide-react-native";
import React, { memo } from "react";

import type { KanbanColors } from "@/features/player-kanban/constants/colors";

import { Box } from "@/components/atoms/Box";
import { Icon } from "@/components/atoms/Icon";
import { Input } from "@/components/atoms/Input";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

/*
 * design-system: dynamic-style waiver — data-driven theme colours (column card
 * background/border/text) that no semantic token expresses here; passed as
 * single style references so no inline object literal renders on a JSX element.
 */
const inputBoxStyle = (cardBackground: string, primary: string) => ({
  backgroundColor: cardBackground,
  borderWidth: 1,
  borderColor: primary,
});
const colorStyle = (color: string) => ({ color });

/**
 *
 */
interface KanbanColumnHeaderViewProps {
  title: string;
  isEditingTitle: boolean;
  editTitleValue: string;
  colors: KanbanColors;
  onSaveTitleEdit: () => void;
  onCancelTitleEdit: () => void;
  onTitleChange: (value: string) => void;
}

/**
 * View component for the kanban column header title.
 *
 * SE-6489: the inline pencil (edit) and trash (delete) icons that used to sit
 * beside the title moved into the stage overflow menu (`StageOptionsMenu`),
 * matching the approved design and putting the destructive action behind a
 * deliberate second interaction rather than one mis-click away while dragging
 * cards. Neither action was dropped — the menu's "Rename Stage" row drives the
 * very same inline edit rendered below, and "Delete Stage" calls the same
 * handler the trash icon called.
 * @param root0 - The component props
 * @param root0.title - The current title text displayed in the column header
 * @param root0.isEditingTitle - Whether the title is currently in edit mode
 * @param root0.editTitleValue - The temporary value of the title while editing
 * @param root0.colors - Kanban color theme object for consistent styling
 * @param root0.onSaveTitleEdit - Callback invoked when user clicks the save button
 * @param root0.onCancelTitleEdit - Callback invoked when user clicks the cancel button
 * @param root0.onTitleChange - Callback invoked when the title input text changes
 */
const KanbanColumnHeaderView = ({
  title,
  isEditingTitle,
  editTitleValue,
  colors,
  onSaveTitleEdit,
  onCancelTitleEdit,
  onTitleChange,
}: KanbanColumnHeaderViewProps) =>
  isEditingTitle ? (
    <Stack direction="horizontal" className="flex-1 items-center gap-2">
      <Input
        className="flex-1 rounded-sm"
        // eslint-disable-next-line no-restricted-syntax -- data-driven column-card bg/border (KanbanColors); no closed token expresses these theme colors
        UNSAFE_style={inputBoxStyle(colors.cardBackground, colors.primary)}
        // The edit field now renders the same text style as the title it
        // replaces (the non-editing branch below is `Text variant="title-sm"`),
        // instead of a raw weight utility on an untyped inherited family.
        textVariant="title-sm"
        field={{
          value: editTitleValue,
          onChangeText: onTitleChange,
          className: "px-2 py-1",
          style: colorStyle(colors.textPrimary),
        }}
      />
      <Pressable
        testID={TEST_ID_PLAYER_KANBAN.SAVE_COLUMN_BUTTON}
        onPress={onSaveTitleEdit}
        className="p-1"
      >
        <Icon as={Check} size="xs" color={colors.success} />
      </Pressable>
      <Pressable
        testID={TEST_ID_PLAYER_KANBAN.CANCEL_COLUMN_BUTTON}
        onPress={onCancelTitleEdit}
        className="p-1"
      >
        <Icon as={X} size="xs" color={colors.error} />
      </Pressable>
    </Stack>
  ) : (
    <Box testID={TEST_ID_PLAYER_KANBAN.COLUMN_HEADER}>
      <Text variant="title-sm" className="text-content-primary">
        {title}
      </Text>
    </Box>
  );

KanbanColumnHeaderView.displayName = "KanbanColumnHeaderView";

export default memo(KanbanColumnHeaderView);
